import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MessageSquare,
  Plus,
  ArrowLeft,
  Send,
  Clock,
  AlertCircle,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Loader2,
  Image,
  X,
} from 'lucide-react';
import { ticketApi, Ticket, TicketAttachment } from '../../api';
import { useTelegram } from '../../hooks';
import toast from 'react-hot-toast';

type ViewMode = 'list' | 'detail' | 'create';

const STATUS_CONFIG: Record<string, { color: string; icon: typeof Clock; label: string }> = {
  open: { color: 'bg-blue-500', icon: Clock, label: 'Open' },
  pending: { color: 'bg-yellow-500', icon: Clock, label: 'Pending' },
  'in-progress': { color: 'bg-purple-500', icon: AlertCircle, label: 'In Progress' },
  resolved: { color: 'bg-green-500', icon: CheckCircle2, label: 'Resolved' },
  closed: { color: 'bg-gray-500', icon: XCircle, label: 'Closed' },
};

const PRIORITY_CONFIG: Record<string, { color: string; label: string }> = {
  low: { color: 'bg-green-500/20 text-green-400', label: 'Low' },
  medium: { color: 'bg-yellow-500/20 text-yellow-400', label: 'Medium' },
  high: { color: 'bg-orange-500/20 text-orange-400', label: 'High' },
  urgent: { color: 'bg-red-500/20 text-red-400', label: 'Urgent' },
};

// Maximum file size: 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const MAX_FILES = 3;

export function Support() {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Create form state
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [createAttachments, setCreateAttachments] = useState<File[]>([]);
  
  // Reply state
  const [replyMessage, setReplyMessage] = useState('');
  const [replyAttachments, setReplyAttachments] = useState<File[]>([]);
  
  // File input refs
  const createFileInputRef = useRef<HTMLInputElement>(null);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  
  // Image preview modal
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  
  const { hapticFeedback, hapticNotification } = useTelegram();

  useEffect(() => {
    if (viewMode === 'list') {
      loadTickets();
    }
  }, [viewMode, page]);


  // Check URL params for deep linking
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ticketId = params.get('ticket');
    if (ticketId) {
      openTicket(ticketId);
      return;
    }
    
    // Check for pending ticket from Telegram deep link (startapp parameter)
    const pendingTicketId = sessionStorage.getItem('gemifaucet_pending_ticket');
    if (pendingTicketId) {
      console.log('[Support] Opening ticket from deep link:', pendingTicketId);
      sessionStorage.removeItem('gemifaucet_pending_ticket');
      openTicket(pendingTicketId);
    }
  }, []);

  const loadTickets = async () => {
    try {
      setLoading(true);
      console.log('[Support] Loading tickets, page:', page);
      const response = await ticketApi.getMyTickets(page, 10);
      console.log('[Support] Tickets loaded:', response);
      setTickets(response.data);
      setTotalPages(response.pages);
    } catch (error: any) {
      console.error('[Support] Failed to load tickets:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      toast.error(error?.response?.data?.error || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const openTicket = async (ticketId: string) => {
    try {
      setLoading(true);
      console.log('[Support] Opening ticket:', ticketId);
      const response = await ticketApi.getTicket(ticketId);
      console.log('[Support] Ticket loaded:', response);
      setSelectedTicket(response.ticket);
      setViewMode('detail');
    } catch (error: any) {
      console.error('[Support] Failed to load ticket:', {
        error,
        ticketId,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      toast.error(error?.response?.data?.error || 'Ticket not found');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (files: FileList | null, type: 'create' | 'reply') => {
    if (!files) return;
    
    const currentAttachments = type === 'create' ? createAttachments : replyAttachments;
    const setAttachments = type === 'create' ? setCreateAttachments : setReplyAttachments;
    
    const newFiles: File[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Check if file is an image
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image`);
        continue;
      }
      
      // Check file size
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} is too large. Maximum size is 5MB`);
        continue;
      }
      
      // Check max files limit
      if (currentAttachments.length + newFiles.length >= MAX_FILES) {
        toast.error(`Maximum ${MAX_FILES} images allowed`);
        break;
      }
      
      newFiles.push(file);
    }
    
    if (newFiles.length > 0) {
      setAttachments([...currentAttachments, ...newFiles]);
      hapticFeedback('light');
    }
  };
  
  const removeAttachment = (index: number, type: 'create' | 'reply') => {
    if (type === 'create') {
      setCreateAttachments(prev => prev.filter((_, i) => i !== index));
    } else {
      setReplyAttachments(prev => prev.filter((_, i) => i !== index));
    }
    hapticFeedback('light');
  };

  const createTicket = async () => {
    if (!subject.trim() || !description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      hapticFeedback('medium');
      
      console.log('[Support] Creating ticket:', { subject, description, priority, attachments: createAttachments.length });
      
      const response = await ticketApi.createTicket({
        subject: subject.trim(),
        description: description.trim(),
        priority,
        attachments: createAttachments.length > 0 ? createAttachments : undefined,
      });

      console.log('[Support] Create ticket response:', response);

      if (response.success) {
        hapticNotification('success');
        toast.success('Ticket created successfully!');
        setSubject('');
        setDescription('');
        setPriority('medium');
        setCreateAttachments([]);
        setViewMode('list');
        loadTickets();
      } else {
        toast.error(response.error || 'Failed to create ticket');
      }
    } catch (error: any) {
      console.error('[Support] Failed to create ticket:', {
        error,
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });
      toast.error(error?.response?.data?.error || 'Failed to create ticket');
    } finally {
      setSubmitting(false);
    }
  };

  const sendReply = async () => {
    if (!replyMessage.trim() || !selectedTicket) return;

    try {
      setSubmitting(true);
      hapticFeedback('light');
      
      const response = await ticketApi.addReply(
        selectedTicket.ticketId, 
        replyMessage.trim(),
        replyAttachments.length > 0 ? replyAttachments : undefined
      );

      if (response.success) {
        setSelectedTicket(response.ticket);
        setReplyMessage('');
        setReplyAttachments([]);
        hapticNotification('success');
        toast.success('Reply sent!');
      } else {
        toast.error(response.error || 'Failed to send reply');
      }
    } catch (error) {
      console.error('Failed to send reply:', error);
      toast.error('Failed to send reply');
    } finally {
      setSubmitting(false);
    }
  };
  
  // Render attachment previews
  const renderAttachmentPreviews = (attachments: File[], type: 'create' | 'reply') => {
    if (attachments.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {attachments.map((file, index) => (
          <div key={index} className="relative group">
            <img
              src={URL.createObjectURL(file)}
              alt={file.name}
              className="w-16 h-16 object-cover rounded-lg border border-white/20"
            />
            <button
              onClick={() => removeAttachment(index, type)}
              className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X className="w-3 h-3 text-white" />
            </button>
          </div>
        ))}
      </div>
    );
  };
  
  // Render saved attachments from ticket/reply (Cloudinary URLs)
  const renderSavedAttachments = (attachments?: TicketAttachment[]) => {
    if (!attachments || attachments.length === 0) return null;
    
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {attachments.map((attachment, index) => (
          <button
            key={attachment._id || index}
            onClick={() => setPreviewImage(attachment.url)}
            className="relative group"
          >
            <img
              src={attachment.url}
              alt={attachment.filename}
              className="w-16 h-16 object-cover rounded-lg border border-white/20 hover:border-primary transition-colors"
            />
          </button>
        ))}
      </div>
    );
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days < 7) {
      return `${days} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // ==================== TICKET LIST VIEW ====================
  const renderTicketList = () => (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 20 }}
      className="space-y-4"
    >
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold">Support Tickets</h2>
        <button
          onClick={() => {
            hapticFeedback('light');
            setViewMode('create');
          }}
          className="btn-primary flex items-center gap-2 px-4 py-2 rounded-xl text-sm"
        >
          <Plus className="w-4 h-4" />
          New Ticket
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : tickets.length === 0 ? (
        <div className="card p-8 text-center">
          <MessageSquare className="w-12 h-12 mx-auto mb-4 text-white/30" />
          <h3 className="text-lg font-medium mb-2">No Tickets Yet</h3>
          <p className="text-white/50 text-sm mb-4">
            Create a support ticket if you need help
          </p>
          <button
            onClick={() => {
              hapticFeedback('light');
              setViewMode('create');
            }}
            className="btn-primary px-6 py-2 rounded-xl text-sm"
          >
            Create Ticket
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((ticket) => {
            const statusConfig = STATUS_CONFIG[ticket.status];
            const priorityConfig = PRIORITY_CONFIG[ticket.priority];

            return (
              <motion.button
                key={ticket.ticketId}
                onClick={() => openTicket(ticket.ticketId)}
                className="card p-4 w-full text-left hover:bg-white/10 transition-colors"
                whileTap={{ scale: 0.98 }}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs text-white/50 font-mono">
                        {ticket.ticketId}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${priorityConfig?.color}`}>
                        {priorityConfig?.label}
                      </span>
                    </div>
                    <h3 className="font-medium line-clamp-1">{ticket.subject}</h3>
                  </div>
                  <ChevronRight className="w-5 h-5 text-white/30 flex-shrink-0" />
                </div>
                <p className="text-sm text-white/50 line-clamp-2 mb-3">
                  {ticket.description}
                </p>
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${statusConfig?.color}`} />
                    <span className="text-white/70">{statusConfig?.label}</span>
                  </div>
                  <span className="text-white/40">{formatDate(ticket.createdAt)}</span>
                </div>
                {ticket.replies.length > 0 && (
                  <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2 text-xs text-white/50">
                    <MessageSquare className="w-3 h-3" />
                    {ticket.replies.length} {ticket.replies.length === 1 ? 'reply' : 'replies'}
                  </div>
                )}
              </motion.button>
            );
          })}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary px-4 py-2 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-white/50">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary px-4 py-2 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );

  // ==================== CREATE TICKET VIEW ====================
  const renderCreateForm = () => (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-4"
    >
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => {
            hapticFeedback('light');
            setViewMode('list');
          }}
          className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold">Create Ticket</h2>
      </div>

      <div className="card p-4 space-y-4">
        <div>
          <label className="block text-sm text-white/70 mb-2">Subject *</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Brief description of your issue"
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary"
            maxLength={200}
          />
        </div>

        <div>
          <label className="block text-sm text-white/70 mb-2">Priority</label>
          <div className="grid grid-cols-4 gap-2">
            {(['low', 'medium', 'high', 'urgent'] as const).map((p) => (
              <button
                key={p}
                onClick={() => setPriority(p)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  priority === p
                    ? PRIORITY_CONFIG[p].color.replace('/20', '')
                    : 'bg-white/10 text-white/50 hover:bg-white/20'
                }`}
              >
                {PRIORITY_CONFIG[p].label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm text-white/70 mb-2">Description *</label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Please describe your issue in detail..."
            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary min-h-[150px] resize-none"
            maxLength={5000}
          />
          <div className="text-right text-xs text-white/40 mt-1">
            {description.length}/5000
          </div>
        </div>

        {/* Attachments */}
        <div>
          <label className="block text-sm text-white/70 mb-2">
            Attachments (optional, max {MAX_FILES} images)
          </label>
          <input
            ref={createFileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFileSelect(e.target.files, 'create')}
            className="hidden"
          />
          <button
            onClick={() => createFileInputRef.current?.click()}
            disabled={createAttachments.length >= MAX_FILES}
            className="flex items-center gap-2 px-4 py-2 bg-white/10 rounded-lg text-sm hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            <Image className="w-4 h-4" />
            Add Photo
          </button>
          {renderAttachmentPreviews(createAttachments, 'create')}
        </div>

        <button
          onClick={createTicket}
          disabled={submitting || !subject.trim() || !description.trim()}
          className="btn-primary w-full py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Send className="w-5 h-5" />
              Submit Ticket
            </>
          )}
        </button>
      </div>
    </motion.div>
  );

  // ==================== TICKET DETAIL VIEW ====================
  const renderTicketDetail = () => {
    if (!selectedTicket) return null;

    const statusConfig = STATUS_CONFIG[selectedTicket.status];
    const priorityConfig = PRIORITY_CONFIG[selectedTicket.priority];

    return (
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-3 mb-4">
          <button
            onClick={() => {
              hapticFeedback('light');
              setSelectedTicket(null);
              setViewMode('list');
            }}
            className="p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <div className="text-xs text-white/50 font-mono">{selectedTicket.ticketId}</div>
            <h2 className="text-lg font-bold line-clamp-1">{selectedTicket.subject}</h2>
          </div>
        </div>

        {/* Status & Priority */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${statusConfig?.color}`} />
              <span className="text-sm font-medium">{statusConfig?.label}</span>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs ${priorityConfig?.color}`}>
              {priorityConfig?.label} Priority
            </span>
          </div>
          <p className="text-sm text-white/70">{selectedTicket.description}</p>
          {/* Show original ticket attachments */}
          {renderSavedAttachments(selectedTicket.attachments)}
          <div className="mt-3 pt-3 border-t border-white/10 text-xs text-white/50">
            Created {formatDate(selectedTicket.createdAt)}
          </div>
        </div>

        {/* Conversation Thread */}
        <div className="card p-4">
          <h3 className="font-medium mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Conversation
          </h3>

          {selectedTicket.replies.length === 0 ? (
            <p className="text-sm text-white/50 text-center py-4">
              No replies yet. Our support team will respond soon.
            </p>
          ) : (
            <div className="space-y-4 max-h-[300px] overflow-y-auto">
              {selectedTicket.replies.map((reply, index) => (
                <div
                  key={reply._id || index}
                  className={`flex ${reply.isAdmin ? 'justify-start' : 'justify-end'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-xl p-3 ${
                      reply.isAdmin
                        ? 'bg-primary/20 rounded-tl-none'
                        : 'bg-white/10 rounded-tr-none'
                    }`}
                  >
                    {reply.isAdmin && (
                      <div className="text-xs text-primary mb-1 font-medium">
                        {reply.adminName || 'Support Team'}
                      </div>
                    )}
                    <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                    {/* Show reply attachments */}
                    {renderSavedAttachments(reply.attachments)}
                    <div className="text-xs text-white/40 mt-1 text-right">
                      {formatDate(reply.timestamp)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Reply Input */}
        {selectedTicket.status !== 'closed' && (
          <div className="card p-4">
            <input
              ref={replyFileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileSelect(e.target.files, 'reply')}
              className="hidden"
            />
            <div className="flex gap-2">
              <textarea
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-primary min-h-[80px] resize-none text-sm"
                maxLength={2000}
              />
            </div>
            {renderAttachmentPreviews(replyAttachments, 'reply')}
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => replyFileInputRef.current?.click()}
                disabled={replyAttachments.length >= MAX_FILES}
                className="p-3 bg-white/10 rounded-xl hover:bg-white/20 transition-colors disabled:opacity-50"
                title="Add photo"
              >
                <Image className="w-5 h-5" />
              </button>
              <button
                onClick={sendReply}
                disabled={submitting || !replyMessage.trim()}
                className="btn-primary flex-1 py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    Send Reply
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {selectedTicket.status === 'closed' && (
          <div className="card p-4 bg-white/5 text-center">
            <p className="text-sm text-white/50">
              This ticket is closed. Create a new ticket if you need further assistance.
            </p>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <div className="pb-4">
      <AnimatePresence mode="wait">
        {viewMode === 'list' && renderTicketList()}
        {viewMode === 'create' && renderCreateForm()}
        {viewMode === 'detail' && renderTicketDetail()}
      </AnimatePresence>
      
      {/* Image Preview Modal */}
      <AnimatePresence>
        {previewImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
            onClick={() => setPreviewImage(null)}
          >
            <button
              className="absolute top-4 right-4 p-2 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
              onClick={() => setPreviewImage(null)}
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
